import { memo, useMemo } from 'react';

import { Check } from '@stratumn/atomic';

import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';

export const HEADER_CHECKBOX = 'CHECKBOX';
export const HEADER_CHECKBOX_INDETERMINATE = 'CHECKBOX_INDETERMINATE';

type Props = {
  className?: string;
  label?: string;
  icon: string;
  iconIfClicked?: string;
  clicked: boolean;
  onClick?: () => void;
  tooltip?: string;
  // When true the element is greyed out and non-interactive; pair with `tooltip`
  // to explain why (e.g. a batch is already running).
  disabled?: boolean;
};

export const TableHeaderElement = memo(
  ({
    className,
    label,
    icon,
    iconIfClicked,
    clicked,
    onClick = () => {},
    tooltip,
    disabled = false
  }: Props) => {
    const isCheckbox = useMemo(() => {
      const uppercaseIconName = icon.toUpperCase();
      return (
        uppercaseIconName === HEADER_CHECKBOX ||
        uppercaseIconName === HEADER_CHECKBOX_INDETERMINATE
      );
    }, [icon]);

    const getIconComponent = () => {
      if (isCheckbox) {
        return <Check checked={clicked} label="" showLabel={false} />;
      }
      return (
        <div className="block shrink-0">
          <DynamicIcon
            icon={clicked && iconIfClicked ? iconIfClicked : icon}
            size={20}
          />
        </div>
      );
    };

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              aria-disabled={disabled}
              className={cn(
                'flex cursor-pointer select-none flex-row flex-nowrap items-center rounded-sm px-2 py-1 text-accent-foreground text-xs outline outline-transparent hover:bg-accent hover:text-accent-foreground hover:outline-accent',
                clicked && 'text-primary',
                isCheckbox && 'mr-2 hover:outline-transparent',
                disabled &&
                  'cursor-not-allowed opacity-40 hover:bg-transparent hover:text-accent-foreground hover:outline-transparent',
                className
              )}
              data-cy={`table-header-${(label || icon)
                .replace(' ', '-')
                .toLowerCase()}`}
              onClick={disabled ? undefined : onClick}
            >
              <span className="pointer-events-none">{getIconComponent()}</span>
              {label && (
                <span className="block grow pl-2 text-xs">{label}</span>
              )}
            </div>
          </TooltipTrigger>
          {tooltip && (
            <TooltipContent side="left" variant="contrast">
              {tooltip}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  }
);
